import { FileTypes } from "@/enums";
import { IFile } from "@/interfaces/IFile";
import { useGetSasMutation, useUploadFileMutation } from "@/services";
import { faInbox } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Upload, UploadFile, UploadProps } from "antd";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import * as Styled from "../styled";

const { Dragger } = Upload;

interface IInitiativeDocumentsUploadProps {
  fileList: UploadFile[];
  initiativePublicId: string | undefined;
  placeholderId: string;
  beforeUpload: UploadProps["beforeUpload"];
  onRemove: UploadProps["onRemove"];
  onChange: UploadProps["onChange"];
  onPreview: UploadProps["onPreview"];
}

export const InitiativeDocumentsUpload = ({
  fileList,
  initiativePublicId,
  placeholderId,
  beforeUpload,
  onRemove,
  onChange,
  onPreview,
}: IInitiativeDocumentsUploadProps): React.JSX.Element => {
  const intl = useIntl();
  const [getSas] = useGetSasMutation();
  const [uploadFile] = useUploadFileMutation();

  const uploadDocument: UploadProps["customRequest"] = ({
    file,
    onProgress,
    onSuccess,
  }) => {
    const uploadingFile = file as IFile & File;
    const storageInfo = { name: uploadingFile.name };

    getSas({
      storageInfo,
      intakeId: initiativePublicId || placeholderId,
    })
      .unwrap()
      .then((response) => {
        onProgress?.({ percent: 10, loaded: 0, total: uploadingFile.size });

        return uploadFile({
          file: uploadingFile,
          location: response.data.itemPath,
          isInitiative: false,
          onProgress: ({
            percent,
            loaded,
            total,
          }: {
            percent: number;
            loaded: number;
            total: number;
          }) => {
            const newProgress = {
              percent: (percent / 100) * 90 + 10,
              loaded,
              total,
            };

            if (newProgress.percent === 100) {
              onSuccess?.(uploadingFile);
              return;
            }

            onProgress?.(newProgress);
          },
        })
          .unwrap()
          .then(({ blockIds, location }: { blockIds: number[]; location: string }) => {
            uploadingFile.blobListIds = blockIds.map(String);
            uploadingFile.blobUrl = location;
            uploadingFile.imageUrl = uploadingFile.type?.startsWith("image/") && location;
            uploadingFile.url = `initiatives/${location}`;
            onSuccess?.(uploadingFile);
          })
          .catch(() => {
            toast.error(intl.formatMessage({ id: "general.uploadFile" }));
          });
      });
  };

  return (
    <Styled.FormItem
      label={intl.formatMessage({ id: "initiative.osaStyleDocumentation" })}
      name="documents"
      getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
      rules={[
        {
          required: true,
          validator: (_, value) =>
            Array.isArray(value) && value.length > 0
              ? Promise.resolve()
              : Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: "initiative.documentsRequiredMessage",
                    })
                  )
                ),
        },
      ]}
    >
      <Dragger
        data-test-id="initiative-upload-element"
        fileList={fileList}
        customRequest={uploadDocument}
        multiple
        beforeUpload={beforeUpload}
        onRemove={onRemove}
        onChange={onChange}
        onPreview={onPreview}
        accept={FileTypes.any}
      >
        <Styled.UploadDragIcon>
          <FontAwesomeIcon icon={faInbox} size="xl" />
        </Styled.UploadDragIcon>
        <Styled.UploadText>
          {intl.formatMessage({ id: "general.uploadFile" })}
        </Styled.UploadText>
      </Dragger>
    </Styled.FormItem>
  );
};

export default InitiativeDocumentsUpload;
